import * as turf from "./geo";

const toNumber = (value) => {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : null;
};

const getPoint = (point) => {
  const lat = toNumber(point?.lat);
  const lng = toNumber(point?.lng);
  if (lat === null || lng === null) return null;
  return { lat, lng };
};

export const isPointInsideShape = (point, shape) => {
  const normalizedPoint = getPoint(point);
  if (!normalizedPoint || !shape?.type) return false;

  const pt = turf.point([normalizedPoint.lng, normalizedPoint.lat]);

  if (shape.type === "Polygon" || shape.type === "Rectangle") {
    const coords = (shape.points || [])
      .map((p) => getPoint(p))
      .filter(Boolean)
      .map((p) => [p.lng, p.lat]);
    if (coords.length < 3) return false;

    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      coords.push(first);
    }

    return turf.booleanPointInPolygon(pt, turf.polygon([coords]));
  }

  if (shape.type === "Circle") {
    const center = getPoint(shape.center);
    const radius = toNumber(shape.radius);
    if (!center || radius === null) return false;

    const centerPoint = turf.point([center.lng, center.lat]);
    return turf.distance(pt, centerPoint, { units: "kilometers" }) * 1000 <= radius;
  }

  if (shape.type === "Marker") {
    const marker = getPoint(shape.point);
    if (!marker) return false;

    const markerPoint = turf.point([marker.lng, marker.lat]);
    return turf.distance(pt, markerPoint, { units: "kilometers" }) * 1000 <= 10;
  }

  return false;
};

export const isPointInsideBranchDeliveryCharge = (point, bdc) => {
  if (!bdc?.shapes?.length) return false;
  return bdc.shapes.some((shape) => isPointInsideShape(point, shape));
};

export const findBranchDeliveryChargeByPin = (point, branchDeliveryCharges = []) => {
  return (branchDeliveryCharges || []).find((bdc) =>
    isPointInsideBranchDeliveryCharge(point, bdc),
  );
};

export const hasSameDeliveryChargePrice = (firstBdc, secondBdc) => {
  const firstPrice = toNumber(firstBdc?.price);
  const secondPrice = toNumber(secondBdc?.price);
  if (firstPrice === null || secondPrice === null) return false;
  return Math.abs(firstPrice - secondPrice) < 0.000001;
};

export const getMapPinBranchSelectionChange = ({
  settings,
  address,
  branch,
  branchDeliveryCharges,
}) => {
  if (
    !settings?.enable_map_pin_selection ||
    !settings?.is_branch_based_ordering ||
    !address?.gpsConfirmed ||
    address?.selectedDeliveryType !== 0 ||
    !branch?.bdc ||
    branch?.isPickup
  ) {
    return null;
  }

  const point = getPoint(address);
  if (!point) return null;

  const currentBdc = (branchDeliveryCharges || []).find(
    (bdc) => bdc.id == branch.bdc,
  );
  if (currentBdc && isPointInsideBranchDeliveryCharge(point, currentBdc)) {
    return null;
  }

  const nextBdc = findBranchDeliveryChargeByPin(point, branchDeliveryCharges);
  if (!nextBdc || nextBdc.id == currentBdc?.id) return null;

  return {
    currentBdc,
    nextBdc,
    priceChanged: !currentBdc || !hasSameDeliveryChargePrice(currentBdc, nextBdc),
  };
};
